import {
  type Category,
  parseReported,
  SPEC_PROPERTIES,
  SPEC_UNIT_LABELS,
  type SpecProperty,
  type SpecUnit,
} from "@bl/domain";
import { useState } from "react";
import type { Spec } from "../api";
import { Field } from "../components/ui";
import { specSourceText } from "../format";

/**
 * Specs are editable wherever they appear (Tyler, 2026-09-20). The catalog is a reference that fills
 * them in; the brewer corrects them when adding an item, on the item itself, and again for a delivery,
 * where the values stop being suggestions and become that lot's own.
 *
 * A field takes what a supplier sheet prints: a number, a range ("1.8 - 2.2") or a bound ("< 4.5").
 */
export interface SpecDraft {
  property: string;
  label: string;
  unit: SpecUnit | null;
  kind: "numeric" | "text";
  /** What the tier above says, and where it came from. Null for a spec nobody has set yet. */
  inherited: Spec | null;
  text: string;
}

/** Properties worth offering for a category even when nothing has filled them in yet. */
const SUGGESTED: Partial<Record<Category, SpecProperty[]>> = {
  hop: ["alpha_acid", "beta_acid", "total_oil"],
  yeast: ["cell_density", "attenuation"],
  malt: ["color", "moisture", "protein", "extract_fine"],
  sugar: ["color", "extract_fine"],
};

export function specText(spec: Spec): string {
  if (spec.kind === "text") return spec.text ?? "";
  const { qualifier, min, max } = spec;
  if (qualifier === "range") return `${min} - ${max}`;
  if (qualifier === "lt") return `< ${max}`;
  if (qualifier === "lte") return `<= ${max}`;
  if (qualifier === "gt") return `> ${min}`;
  if (qualifier === "gte") return `>= ${min}`;
  return min ?? "";
}

export function makeDrafts(
  specs: Spec[],
  category?: Category,
  offered: readonly SpecProperty[] = (category && SUGGESTED[category]) ?? [],
): SpecDraft[] {
  const drafts: SpecDraft[] = specs.map((s) => ({
    property: s.property,
    label: s.label,
    unit: s.unit,
    kind: s.kind,
    inherited: s,
    text: specText(s),
  }));
  for (const property of offered) {
    if (drafts.some((d) => d.property === property)) continue;
    const definition = SPEC_PROPERTIES[property];
    drafts.push({
      property,
      label: definition.label,
      unit: definition.units[0] ?? null,
      kind: definition.kind,
      inherited: null,
      text: "",
    });
  }
  return drafts;
}

export interface SpecChanges {
  // biome-ignore lint/suspicious/noExplicitAny: the generated SpecInput union, passed straight through
  specs: any[];
  removeSpecs: string[];
  error: string | null;
}

/** What changed, ready for the API: only fields a person actually touched. */
export function collectSpecs(drafts: SpecDraft[]): SpecChanges {
  // biome-ignore lint/suspicious/noExplicitAny: same
  const specs: any[] = [];
  const removeSpecs: string[] = [];
  for (const draft of drafts) {
    const before = draft.inherited ? specText(draft.inherited) : "";
    const now = draft.text.trim();
    if (now === before.trim()) continue;
    if (!now) {
      if (draft.inherited) removeSpecs.push(draft.property);
      continue;
    }
    if (draft.kind === "text") {
      specs.push({ kind: "text", property: draft.property, text: now });
      continue;
    }
    try {
      const value = parseReported(now, (draft.unit ?? "percent") as SpecUnit);
      if (value?.kind !== "numeric") {
        removeSpecs.push(draft.property);
        continue;
      }
      specs.push({
        kind: "numeric",
        property: draft.property,
        qualifier: value.qualifier,
        min: value.min,
        max: value.max,
        unit: value.unit,
      });
    } catch {
      return {
        specs: [],
        removeSpecs: [],
        error: `${draft.label}: "${now}" is not a number, range or limit`,
      };
    }
  }
  return { specs, removeSpecs, error: null };
}

export function useSpecDrafts(specs: Spec[], category?: Category) {
  const [drafts, setDrafts] = useState<SpecDraft[]>(() => makeDrafts(specs, category));
  const set = (property: string, text: string) =>
    setDrafts((list) => list.map((d) => (d.property === property ? { ...d, text } : d)));
  return { drafts, set };
}

export function SpecFields({
  drafts,
  onChange,
  mainSource,
  markYours = true,
}: {
  drafts: SpecDraft[];
  onChange: (property: string, text: string) => void;
  /** The source named once above the fields; a field says its own only when it differs. */
  mainSource?: string | null;
  /** False where every figure is the brewer's own, on an item made by hand, so saying so is noise. */
  markYours?: boolean;
}) {
  return (
    <div className="form-grid">
      {drafts.map((draft) => {
        const source = draft.inherited?.source ?? null;
        const shown = source && source.name !== mainSource ? specSourceText(source) : null;
        const changed = draft.text.trim() !== (draft.inherited ? specText(draft.inherited) : "");
        return (
          <Field
            key={draft.property}
            label={`${draft.label}${draft.unit && SPEC_UNIT_LABELS[draft.unit] ? ` (${SPEC_UNIT_LABELS[draft.unit]})` : ""}`}
            hint={changed && markYours ? "Yours" : shown}
          >
            <input
              className="input"
              value={draft.text}
              placeholder={draft.kind === "numeric" ? "Value, range or limit" : "Not recorded"}
              onChange={(e) => onChange(draft.property, e.target.value)}
            />
          </Field>
        );
      })}
    </div>
  );
}
